import { useState, useEffect } from 'react';
import { Plus, Calendar, FileText, TrendingUp } from 'lucide-react';
import ProfitLossStatement from '../components/ProfitLoss/ProfitLossStatement';
import api from '../services/api';
import toast from 'react-hot-toast';

interface PLStatement {
  id: string;
  periodStart: string;
  periodEnd: string;
  periodType: string;
  scenario: string;
  totals: {
    totalRevenue: number;
    netIncome: number;
  };
  createdAt: string;
}

const ProfitLoss = () => {
  const [statements, setStatements] = useState<PLStatement[]>([]);
  const [selectedStatement, setSelectedStatement] = useState<string | null>(null);
  const [comparisonStatement, setComparisonStatement] = useState<string | null>(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showVariance, setShowVariance] = useState(false);
  const [loading, setLoading] = useState(false);

  // Form state
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [periodType, setPeriodType] = useState<'monthly' | 'quarterly' | 'yearly'>('monthly');
  const [scenario, setScenario] = useState<'actual' | 'budget' | 'forecast'>('actual');

  useEffect(() => {
    loadStatements();
  }, []);

  const loadStatements = async () => {
    setLoading(true);
    try {
      const response = await api.get('/profit-loss/statements', {
        params: { limit: 20 },
      });
      setStatements(response.data.data);

      // Auto-select the most recent statement
      if (response.data.data.length > 0 && !selectedStatement) {
        setSelectedStatement(response.data.data[0].id);
      }
    } catch (error: any) {
      toast.error('Failed to load P&L statements');
      console.error('Error loading statements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePL = async () => {
    if (!periodStart || !periodEnd) {
      toast.error('Please select period start and end dates');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/profit-loss/generate', {
        periodStart,
        periodEnd,
        periodType,
        scenario,
      });

      toast.success('P&L statement generated successfully!');
      setShowGenerateModal(false);

      // Reload statements and select the new one
      await loadStatements();
      setSelectedStatement(response.data.data.id);

      // Reset form
      setPeriodStart('');
      setPeriodEnd('');
      setPeriodType('monthly');
      setScenario('actual');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to generate P&L statement');
      console.error('Error generating statement:', error);
    } finally {
      setLoading(false);
    }
  };

  const getQuickDateRange = (range: 'mtd' | 'qtd' | 'ytd') => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();

    let start: Date;
    let end = today;

    switch (range) {
      case 'mtd':
        start = new Date(year, month, 1);
        setPeriodType('monthly');
        break;
      case 'qtd':
        const quarter = Math.floor(month / 3);
        start = new Date(year, quarter * 3, 1);
        setPeriodType('quarterly');
        break;
      case 'ytd':
        start = new Date(year, 0, 1);
        setPeriodType('yearly');
        break;
    }

    setPeriodStart(start!.toISOString().split('T')[0]);
    setPeriodEnd(end.toISOString().split('T')[0]);
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 bg-white border-b border-neutral-200">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-neutral-900">Profit & Loss</h1>
            <p className="text-neutral-600 mt-1">
              Generate and analyze profit & loss statements with variance analysis
            </p>
          </div>

          <button
            onClick={() => setShowGenerateModal(true)}
            className="btn btn-primary"
          >
            <Plus className="w-4 h-4 mr-2" />
            Generate P&L
          </button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar - Statement List */}
        <div className="w-80 bg-white border-r border-neutral-200 overflow-y-auto">
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-neutral-900">Recent Statements</h3>
              {statements.length > 0 && (
                <label className="flex items-center space-x-2 text-sm">
                  <input
                    type="checkbox"
                    checked={showVariance}
                    onChange={(e) => setShowVariance(e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-neutral-700">Compare</span>
                </label>
              )}
            </div>

            {loading && statements.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="spinner"></div>
              </div>
            ) : statements.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 mx-auto mb-3 text-neutral-300" />
                <p className="text-sm text-neutral-600">No statements yet</p>
                <button
                  onClick={() => setShowGenerateModal(true)}
                  className="text-sm text-primary-600 hover:text-primary-700 mt-2"
                >
                  Generate your first P&L
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {statements.map((stmt) => (
                  <div
                    key={stmt.id}
                    onClick={() => {
                      if (showVariance && selectedStatement) {
                        setComparisonStatement(stmt.id);
                      } else {
                        setSelectedStatement(stmt.id);
                      }
                    }}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedStatement === stmt.id
                        ? 'border-primary-500 bg-primary-50'
                        : comparisonStatement === stmt.id
                        ? 'border-success-500 bg-success-50'
                        : 'border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-neutral-600 capitalize">
                        {stmt.periodType} · {stmt.scenario}
                      </span>
                      {selectedStatement === stmt.id && (
                        <span className="text-xs bg-primary-600 text-white px-2 py-0.5 rounded">
                          Current
                        </span>
                      )}
                      {comparisonStatement === stmt.id && showVariance && (
                        <span className="text-xs bg-success-600 text-white px-2 py-0.5 rounded">
                          Comparing
                        </span>
                      )}
                    </div>

                    <div className="flex items-center text-sm text-neutral-700 mb-2">
                      <Calendar className="w-3 h-3 mr-1" />
                      <span>
                        {new Date(stmt.periodStart).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                        })}{' '}
                        -{' '}
                        {new Date(stmt.periodEnd).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </span>
                    </div>

                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-neutral-600">Revenue:</span>
                        <span className="font-medium text-neutral-900">
                          {formatCurrency(stmt.totals.totalRevenue)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-neutral-600">Net Income:</span>
                        <span
                          className={`font-medium ${
                            stmt.totals.netIncome >= 0 ? 'text-success-600' : 'text-alert-600'
                          }`}
                        >
                          {formatCurrency(stmt.totals.netIncome)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Main Content - Statement Display */}
        <div className="flex-1 p-6 bg-neutral-50 overflow-y-auto">
          <ProfitLossStatement
            statementId={selectedStatement || undefined}
            showVariance={showVariance && !!comparisonStatement}
            comparisonStatementId={comparisonStatement || undefined}
            onGenerate={() => setShowGenerateModal(true)}
          />
        </div>
      </div>

      {/* Generate Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
            <div className="p-6 border-b border-neutral-200">
              <h2 className="text-xl font-bold text-neutral-900">Generate P&L Statement</h2>
              <p className="text-sm text-neutral-600 mt-1">
                Select period and scenario to generate a profit & loss statement
              </p>
            </div>

            <div className="p-6 space-y-6">
              {/* Quick Date Ranges */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Quick Select
                </label>
                <div className="flex space-x-2">
                  <button
                    onClick={() => getQuickDateRange('mtd')}
                    className="btn btn-secondary text-sm"
                  >
                    Month to Date
                  </button>
                  <button
                    onClick={() => getQuickDateRange('qtd')}
                    className="btn btn-secondary text-sm"
                  >
                    Quarter to Date
                  </button>
                  <button
                    onClick={() => getQuickDateRange('ytd')}
                    className="btn btn-secondary text-sm"
                  >
                    Year to Date
                  </button>
                </div>
              </div>

              {/* Period Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Period Start
                  </label>
                  <input
                    type="date"
                    value={periodStart}
                    onChange={(e) => setPeriodStart(e.target.value)}
                    className="input w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Period End
                  </label>
                  <input
                    type="date"
                    value={periodEnd}
                    onChange={(e) => setPeriodEnd(e.target.value)}
                    className="input w-full"
                  />
                </div>
              </div>

              {/* Period Type */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Period Type
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {(['monthly', 'quarterly', 'yearly'] as const).map((type) => (
                    <button
                      key={type}
                      onClick={() => setPeriodType(type)}
                      className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                        periodType === type
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-neutral-300 bg-white text-neutral-700 hover:border-neutral-400'
                      }`}
                    >
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Scenario */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Scenario
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {(['actual', 'budget', 'forecast'] as const).map((scen) => (
                    <button
                      key={scen}
                      onClick={() => setScenario(scen)}
                      className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                        scenario === scen
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-neutral-300 bg-white text-neutral-700 hover:border-neutral-400'
                      }`}
                    >
                      {scen.charAt(0).toUpperCase() + scen.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-neutral-200 flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowGenerateModal(false)}
                className="btn btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleGeneratePL}
                className="btn btn-primary"
                disabled={loading || !periodStart || !periodEnd}
              >
                {loading ? (
                  <>
                    <div className="spinner w-4 h-4 mr-2"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Generate Statement
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfitLoss;
